import React, { useState, useMemo, useEffect } from 'react'
import { Text, Flexbox, Search, Icon } from '@components/common'
import { AssetValidationError } from '@api/Import'

interface ValidationResultsProps {
  errors: AssetValidationError[]
  totalErrors: number
}

const ValidationResults: React.FC<ValidationResultsProps> = ({ errors, totalErrors }) => {
  const [search, setSearch] = useState('')
  const [openIndices, setOpenIndices] = useState<Set<number>>(new Set())

  const affectedAssets = errors?.length ?? 0

  const filteredErrors = useMemo(() => {
    if (!errors || errors.length === 0) return []
    const term = search.trim().toLowerCase()
    if (!term) return errors
    return errors.filter(e => e.AssetId.toLowerCase().includes(term))
  }, [errors, search])

  // Keep all items open by default; re-open all when search reveals new items
  useEffect(() => {
    setOpenIndices(new Set(filteredErrors.map((_, i) => i)))
  }, [filteredErrors])

  const toggleIndex = (i: number) => {
    setOpenIndices(prev => {
      const next = new Set(prev)
      next.has(i) ? next.delete(i) : next.add(i)
      return next
    })
  }

  /* ─── SUCCESS STATE ───────────────────────────────────────────────────── */
  if (!errors || errors.length === 0) {
    return (
      <div
        style={{
          padding: '24px 20px',
          borderRadius: '8px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          textAlign: 'center',
        }}
      >
        <Flexbox alignItems="center" justifyContent="center" flexDirection="column" gap={10}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#22c55e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <Icon name="Check" size="l" />
          </div>
          <div>
            <Text bold style={{ fontSize: '15px', color: '#15803d', display: 'block', marginBottom: '4px' }}>
              Validation Passed
            </Text>
            <Text style={{ color: '#166534', fontSize: '13px' }}>
              All assets are valid and ready to be imported.
            </Text>
          </div>
        </Flexbox>
      </div>
    )
  }

  /* ─── ERROR STATE ─────────────────────────────────────────────────────── */
  return (
    <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #fecaca' }}>

      {/* ── Summary Banner ── */}
      <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
        <Flexbox alignItems="center" justifyContent="space-between" gap={12}>
          <Flexbox alignItems="center" gap={10}>
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '50%',
                backgroundColor: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: '#b91c1c',
              }}
            >
              <Icon name="AlertTriangle" size="m" />
            </div>
            <div>
              <Text bold style={{ color: '#b91c1c', fontSize: '14px', display: 'block' }}>
                Validation Failed
              </Text>
              <Text style={{ color: '#991b1b', fontSize: '12px' }}>
                Fix the errors below before uploading.
              </Text>
            </div>
          </Flexbox>

          <Flexbox gap={16} alignItems="center">
            <div style={{ textAlign: 'right' }}>
              <Text bold style={{ fontSize: '20px', color: '#b91c1c', display: 'block', lineHeight: '1.1' }}>
                {totalErrors}
              </Text>
              <Text style={{ fontSize: '11px', color: '#9f1239', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Total Errors
              </Text>
            </div>
            <div style={{ width: '1px', height: '32px', backgroundColor: '#fca5a5' }} />
            <div style={{ textAlign: 'right' }}>
              <Text bold style={{ fontSize: '20px', color: '#b91c1c', display: 'block', lineHeight: '1.1' }}>
                {affectedAssets}
              </Text>
              <Text style={{ fontSize: '11px', color: '#9f1239', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Assets Affected
              </Text>
            </div>
          </Flexbox>
        </Flexbox>
      </div>

      {/* ── Search Bar ── */}
      <div style={{ padding: '10px 16px', backgroundColor: '#fff', borderBottom: '1px solid #fee2e2' }}>
        <Search
          placeholder="Search by Asset ID..."
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          width="100%"
          size="compact"
        />
      </div>

      {/* ── Error List ── */}
      <div style={{ maxHeight: '320px', overflowY: 'auto', backgroundColor: '#fff' }}>
        {filteredErrors.length === 0 ? (
          <Flexbox alignItems="center" justifyContent="center" style={{ padding: '28px 16px' }}>
            <Text style={{ color: '#9ca3af', fontSize: '13px' }}>No assets match your search.</Text>
          </Flexbox>
        ) : (
          <ul style={{ listStyle: 'none', margin: '0', padding: '0' }}>
            {filteredErrors.map((error, i) => {
              const isOpen = openIndices.has(i)
              const isLast = i === filteredErrors.length - 1
              return (
                <li
                  key={error.AssetId}
                  style={{ borderBottom: isLast ? 'none' : '1px solid #fee2e2' }}
                >
                  {/* Row header */}
                  <button
                    type="button"
                    onClick={() => toggleIndex(i)}
                    style={{
                      width: '93%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 16px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      gap: '8px',
                    }}
                  >
                    <Flexbox alignItems="center" gap={8}>
                      <Text bold style={{ fontSize: '13px', color: '#111827' }}>
                        {error.AssetId}
                      </Text>
                      <span
                        style={{
                          fontSize: '11px',
                          color: '#ef4444',
                          fontWeight: '500',
                          backgroundColor: '#fee2e2',
                          borderRadius: '10px',
                          padding: '1px 7px',
                        }}
                      >
                        {error.Errors.length} {error.Errors.length === 1 ? 'error' : 'errors'}
                      </span>
                    </Flexbox>
                    <Icon
                      name="ChevronDown"
                      size="s"
                      style={{
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 150ms ease',
                        flexShrink: 0,
                      }}
                    />
                  </button>

                  {/* Collapsible error messages */}
                  {isOpen && (
                    <div style={{ padding: '0 16px 12px 16px' }}>
                      {error.Errors.map((msg, j) => (
                        <Flexbox
                          key={j}
                          alignItems="flex-start"
                          gap={8}
                          style={{ marginBottom: j < error.Errors.length - 1 ? '6px' : '0' }}
                        >
                          <span
                            style={{
                              color: '#ef4444',
                              fontWeight: '700',
                              flexShrink: 0,
                              lineHeight: '1.5',
                              fontSize: '14px',
                            }}
                          >
                            ·
                          </span>
                          <Text style={{ color: '#dc2626', fontSize: '13px', lineHeight: '1.5', overflowWrap: 'break-word', minWidth: '0' }}>
                            {msg}
                          </Text>
                        </Flexbox>
                      ))}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* ── Footer: filtered indicator ── */}
      {search.trim() && filteredErrors.length < affectedAssets && (
        <div style={{ padding: '7px 16px', backgroundColor: '#fef2f2', borderTop: '1px solid #fecaca' }}>
          <Text style={{ fontSize: '12px', color: '#9ca3af' }}>
            Showing{' '}
            <strong style={{ color: '#b91c1c' }}>{filteredErrors.length}</strong>
            {' '}of{' '}
            <strong style={{ color: '#b91c1c' }}>{affectedAssets}</strong>
            {' '}assets
          </Text>
        </div>
      )}
    </div>
  )
}

export default ValidationResults
